import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import type { DisplaySettings, Project, Reanchor, ScrollState } from '@goanna-im/shared';

const FONT_STACK: Record<DisplaySettings['fontFamily'], string> = {
  system: 'system-ui, sans-serif',
  serif: 'Georgia, "Times New Roman", serif',
  mono: 'ui-monospace, "Cascadia Code", Consolas, monospace',
  humanist: '"Segoe UI", system-ui, sans-serif',
  condensed: '"Arial Narrow", "Segoe UI", sans-serif',
};

const CONTRAST_COLOUR: Record<DisplaySettings['contrast'], string> = {
  standard: '#ffffff',
  high: '#ffffff',
  soft: '#d7dde3',
};

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

/**
 * The scrolling script. Motion is integrated from real elapsed time (frame-rate
 * independent). Position is anchored to a BLOCK, so live edits and font/layout
 * changes keep the reading line on the same words. The presenter reports its
 * concrete anchor back to the server via onReanchor.
 */
export function ScriptStage({
  project,
  scroll,
  settings,
  onReanchor,
}: {
  project: Project;
  scroll: ScrollState;
  settings: DisplaySettings;
  onReanchor: (r: Reanchor) => void;
}) {
  const zoneRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const posRef = useRef(0);
  const runningRef = useRef(scroll.running);
  const speedRef = useRef(scroll.speedPxPerSec);
  const anchorRef = useRef<Reanchor>({ blockId: scroll.anchor.blockId, offsetFraction: scroll.anchor.offsetFraction });
  const appliedAnchorRef = useRef<Reanchor | null>(null);
  const lastFrameRef = useRef(0);
  const lastEmitRef = useRef(0);
  const glideTargetRef = useRef<number | null>(null); // px to ease toward (voice-follow / jumps)
  const prevRunningRef = useRef(scroll.running);
  const onReanchorRef = useRef(onReanchor);
  onReanchorRef.current = onReanchor;

  const readingLinePx = () => (zoneRef.current?.clientHeight ?? 0) * settings.readingBandPos;
  const contentHeight = () => contentRef.current?.scrollHeight ?? 0;
  const maxPos = () => Math.max(0, contentHeight());

  const blockEl = (blockId: string): HTMLElement | null =>
    contentRef.current?.querySelector<HTMLElement>(`[data-bid="${cssEscape(blockId)}"]`) ?? null;

  const anchorToPx = (a: { blockId: string; offsetFraction: number }): number => {
    const el = blockEl(a.blockId);
    if (!el) return posRef.current;
    return el.offsetTop - readingLinePx() + a.offsetFraction * el.offsetHeight;
  };

  const pxToAnchor = (px: number): Reanchor => {
    const target = px + readingLinePx();
    const els = Array.from(contentRef.current?.querySelectorAll<HTMLElement>('[data-bid]') ?? []);
    if (els.length === 0) return anchorRef.current;
    if (target <= els[0].offsetTop) return { blockId: els[0].dataset.bid!, offsetFraction: 0 };
    for (const el of els) {
      if (target >= el.offsetTop && target < el.offsetTop + el.offsetHeight) {
        const offsetFraction = clamp((target - el.offsetTop) / Math.max(1, el.offsetHeight), 0, 1);
        return { blockId: el.dataset.bid!, offsetFraction };
      }
    }
    const last = els[els.length - 1];
    return { blockId: last.dataset.bid!, offsetFraction: 1 };
  };

  const apply = () => {
    if (contentRef.current) {
      contentRef.current.style.transform = `translateY(${-posRef.current}px)`;
    }
  };

  // Animation loop — integrate position from elapsed time only.
  useEffect(() => {
    let raf = 0;
    const tick = (ts: number) => {
      const dt = lastFrameRef.current ? (ts - lastFrameRef.current) / 1000 : 0;
      lastFrameRef.current = ts;

      if (glideTargetRef.current !== null) {
        // Ease toward a target (voice-follow / section jumps) — never snap.
        const diff = glideTargetRef.current - posRef.current;
        if (Math.abs(diff) < 1) {
          posRef.current = glideTargetRef.current;
          glideTargetRef.current = null;
        } else {
          posRef.current = clamp(posRef.current + diff * Math.min(1, dt * 5), 0, maxPos());
        }
        apply();
      } else if (runningRef.current && speedRef.current > 0) {
        posRef.current = clamp(posRef.current + speedRef.current * dt, 0, maxPos());
        apply();
        if (ts - lastEmitRef.current > 1500) {
          lastEmitRef.current = ts;
          const a = pxToAnchor(posRef.current);
          anchorRef.current = a;
          appliedAnchorRef.current = a;
          onReanchorRef.current(a);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to authoritative scroll changes: jumps snap; play/pause/speed continue.
  useEffect(() => {
    runningRef.current = scroll.running;
    speedRef.current = scroll.speedPxPerSec;
    const incoming = { blockId: scroll.anchor.blockId, offsetFraction: scroll.anchor.offsetFraction };
    const applied = appliedAnchorRef.current;
    const isJump =
      !applied ||
      applied.blockId !== incoming.blockId ||
      Math.abs(applied.offsetFraction - incoming.offsetFraction) > 0.03;

    if (isJump) {
      const targetPx = clamp(anchorToPx(incoming), 0, maxPos());
      if (applied === null) {
        // First render / initial position — snap, don't glide from 0.
        posRef.current = targetPx;
        glideTargetRef.current = null;
        apply();
      } else {
        // Glide toward the new position (voice-follow, section jumps, goto).
        glideTargetRef.current = targetPx;
      }
      anchorRef.current = incoming;
    }
    appliedAnchorRef.current = incoming;

    // Only on an actual running -> paused transition, report the drifted position
    // (for late joiners). Not on every non-running update, or it fights the glide.
    if (prevRunningRef.current && !scroll.running) {
      const a = pxToAnchor(posRef.current);
      anchorRef.current = a;
      appliedAnchorRef.current = a;
      onReanchorRef.current(a);
    }
    prevRunningRef.current = scroll.running;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scroll]);

  // Preserve reading position across live edits / layout / font changes.
  useLayoutEffect(() => {
    posRef.current = clamp(anchorToPx(anchorRef.current), 0, maxPos());
    apply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, settings.fontSizePx, settings.lineHeight, settings.fontFamily, settings.readingBandPos, settings.marginLeftPct, settings.marginRightPct]);

  const contentStyle = useMemo<React.CSSProperties>(
    () => ({
      fontSize: `${settings.fontSizePx}px`,
      lineHeight: settings.lineHeight,
      fontFamily: FONT_STACK[settings.fontFamily],
      textAlign: settings.align,
      textTransform: settings.uppercase ? 'uppercase' : 'none',
      color: CONTRAST_COLOUR[settings.contrast],
      paddingLeft: `${settings.marginLeftPct}%`,
      paddingRight: `${settings.marginRightPct}%`,
      paddingTop: `${settings.readingBandPos * 100}%`,
      paddingBottom: '60%',
      opacity: settings.brightnessFriendly ? 0.82 : 1,
    }),
    [settings],
  );

  return (
    <div className="script-zone" ref={zoneRef}>
      {settings.fadeTopPct > 0 && <div className="fade-top" style={{ height: `${settings.fadeTopPct}%` }} />}
      {settings.showReadingLine && (
        <div
          className="reading-band"
          style={{
            top: `${settings.readingBandPos * 100}%`,
            height: `${settings.fontSizePx * settings.lineHeight * 1.3}px`,
          }}
        />
      )}
      <div className="script-content" ref={contentRef} style={contentStyle}>
        {project.sections.map((section) => (
          <div key={section.id}>
            {section.heading.trim() !== '' && (
              <div className="blk section-head" style={{ marginBottom: '0.3em' }}>
                {section.heading}
              </div>
            )}
            {section.blocks.map((block) => (
              <div key={block.id} data-bid={block.id} className={`blk ${block.type}`} style={{ marginBottom: '0.55em' }}>
                {block.type === 'marker' || block.type === 'direction' ? `[ ${block.text} ]` : block.text}
              </div>
            ))}
          </div>
        ))}
      </div>
      {settings.fadeBottomPct > 0 && (
        <div className="fade-bottom" style={{ height: `${settings.fadeBottomPct}%` }} />
      )}
    </div>
  );
}

/** CSS.escape fallback for older engines. */
function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(value);
  return value.replace(/["\\]/g, '\\$&');
}
